import { SupportedNetwork } from '@spritz-finance/sdk'
import { Multicall } from 'ethereum-multicall'
import { Maybe } from 'graphql/jsutils/Maybe'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { getRpcProviderForNetwork } from '../../../../../web3/connection/providers'
import { ERC20 } from '../../../../../web3/hooks/interfaces'

export type TokenData = { decimals: number; symbol: string } | null

type State = {
    loading: boolean
    token: TokenData
}

const initialState = {
    token: null,
    loading: false,
}

export const useTokenData = (tokenAddress?: string, network?: Maybe<SupportedNetwork>) => {
    const callKey = useRef<string | null>(null)
    const [state, setState] = useState<State>(initialState)

    const provider = useMemo(() => (network ? getRpcProviderForNetwork(network) : null), [network])
    const multicall = useMemo(
        () => (provider ? new Multicall({ ethersProvider: provider, tryAggregate: true }) : null),
        [provider]
    )

    const getTokenData = useCallback(async () => {
        if (!tokenAddress || !network || !multicall) return null
        setState((curr) => ({ ...curr, loading: true }))

        callKey.current = `${tokenAddress.toLowerCase()}:${network}`

        const {
            results: { tokenData },
        } = await multicall.call([
            {
                reference: 'tokenData',
                contractAddress: tokenAddress,
                abi: ERC20.abi,
                calls: [
                    {
                        reference: 'decimals',
                        methodName: 'decimals()',
                        methodParameters: [],
                    },
                    {
                        reference: 'symbol',
                        methodName: 'symbol()',
                        methodParameters: [],
                    },
                ],
            },
        ])

        const data = tokenData.callsReturnContext.reduce((acc, context) => {
            acc[context.reference] = context.returnValues[0]
            return acc
        }, {})

        setState({ token: data as { decimals: number; symbol: string }, loading: false })
    }, [multicall, network, tokenAddress])

    useEffect(() => {
        if (!tokenAddress || callKey.current === `${tokenAddress?.toLowerCase()}:${network}`) return
        getTokenData()
    }, [tokenAddress, network, getTokenData])

    return {
        tokenData: state.token,
        loading: state.loading,
    }
}
